/******
 * This file was generated by a Rake task.
 * Do not modify this file manually.
 * Add changes to the `Delivery.ts` file.
 ******/
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
dayjs.extend(utc);
dayjs.extend(timezone);

import { Delivery } from "./Delivery";

import { Audit } from "./Audit";
import { CalendarEvent } from "./CalendarEvent";
import { Order } from "./Order";
import { Project } from "./Project";

export type status = "declined" | "requested" | "scheduled" | "en_route" | "arrived" | "departed";

export const statusValues = { declined: 0, requested: 1, scheduled: 2, en_route: 3, arrived: 4, departed: 5 };

export abstract class DeliveryModel {
    id: number;
    archived: boolean | null;
    createdAt: Dayjs;
    deliveryRoute: string[] | null;
    eta: Dayjs | null;
    etaSelected: number | null;
    idleTime: number | null;
    integrationLogId: number | null;
    projectId: number | null;
    status: status;
    travelDistance: number | null;
    uniqueToken: string;
    updatedAt: Dayjs;
    vehicleQuantity: number;
    vehicleType: string | null;
    vendorDirections: any | null;
    vendorTravelDistance: number | null;
    vendorTravelTime: number | null;
    receivingTeamLeads: any | null;
    audits: Audit[] | undefined;
    calendarEvent: CalendarEvent | null | undefined;
    orders: Order[] | undefined;
    project: Project | null | undefined;
    _destroy: boolean | undefined;

    constructor(
        id: number,
        archived: boolean | null,
        createdAt: Dayjs,
        deliveryRoute: string[] | null,
        eta: Dayjs | null,
        etaSelected: number | null,
        idleTime: number | null,
        integrationLogId: number | null,
        projectId: number | null,
        status: status,
        travelDistance: number | null,
        uniqueToken: string,
        updatedAt: Dayjs,
        vehicleQuantity: number,
        vehicleType: string | null,
        vendorDirections: any | null,
        vendorTravelDistance: number | null,
        vendorTravelTime: number | null,
        receivingTeamLeads: any | null,
        audits: Audit[] | undefined,
        calendarEvent: CalendarEvent | null | undefined,
        orders: Order[] | undefined,
        project: Project | null | undefined
    ) {
        this.id = id;
        this.archived = archived;
        this.createdAt = createdAt;
        this.deliveryRoute = deliveryRoute;
        this.eta = eta;
        this.etaSelected = etaSelected;
        this.idleTime = idleTime;
        this.integrationLogId = integrationLogId;
        this.projectId = projectId;
        this.status = status;
        this.travelDistance = travelDistance;
        this.uniqueToken = uniqueToken;
        this.updatedAt = updatedAt;
        this.vehicleQuantity = vehicleQuantity;
        this.vehicleType = vehicleType;
        this.vendorDirections = vendorDirections;
        this.vendorTravelDistance = vendorTravelDistance;
        this.vendorTravelTime = vendorTravelTime;
        this.receivingTeamLeads = receivingTeamLeads;
        this.audits = audits;
        this.calendarEvent = calendarEvent;
        this.orders = orders;
        this.project = project;
    }

    static fromApiObject(json: any, timezone: string): Delivery {
        const newObject = new Delivery(
            json["id"],
            json["archived"],
            dayjs(json["created_at"]).tz(timezone),
            json["delivery_route"],
            json["eta"] ? dayjs(json["eta"]).tz(timezone) : null,
            json["eta_selected"],
            json["idle_time"],
            json["integration_log_id"],
            json["project_id"],
            json["status"],
            json["travel_distance"],
            json["unique_token"],
            dayjs(json["updated_at"]).tz(timezone),
            json["vehicle_quantity"],
            json["vehicle_type"],
            json["vendor_directions"],
            json["vendor_travel_distance"],
            json["vendor_travel_time"],
            json["receiving_team_leads"],
            json["audits"]?.map((item: any) => Audit.fromApiObject(item, timezone)),
            json["calendar_event"] == null
                ? json["calendar_event"]
                : CalendarEvent.fromApiObject(json["calendar_event"], timezone),
            json["orders"]?.map((item: any) => Order.fromApiObject(item, timezone)),
            json["project"] == null ? json["project"] : Project.fromApiObject(json["project"], timezone)
        );

        return newObject;
    }

    toApiObject(): any {
        return {
            id: this.id,
            archived: this.archived,
            created_at: this.createdAt,
            delivery_route: this.deliveryRoute,
            eta: this.eta,
            eta_selected: this.etaSelected,
            idle_time: this.idleTime,
            integration_log_id: this.integrationLogId,
            project_id: this.projectId,
            status: this.status,
            travel_distance: this.travelDistance,
            unique_token: this.uniqueToken,
            updated_at: this.updatedAt,
            vehicle_quantity: this.vehicleQuantity,
            vehicle_type: this.vehicleType,
            vendor_directions: this.vendorDirections,
            vendor_travel_distance: this.vendorTravelDistance,
            vendor_travel_time: this.vendorTravelTime,
            audits: this.audits?.map((item) => item.toApiObject()),
            orders: this.orders?.map((item) => item.toApiObject()),
            _destroy: this._destroy,
        };
    }

    // NOTE: This could potentially cause infinite loops if the object graph is
    // circular.
    inAlternateTimezone(timezone: string): Delivery {
        const clone = Object.create(this) as Delivery;

        for (const key of Object.keys(this)) {
            if (this[key] instanceof dayjs) {
                // @ts-expect-error: Typescript thinks that this is not a dayjs object even though it is checked on the line before
                clone[key] = this[key].tz(timezone);
            } else if (Array.isArray(this[key])) {
                clone[key] = this[key].map((item) =>
                    item.inAlternateTimezone ? item.inAlternateTimezone(timezone) : item
                );
            } else if (this[key] && this[key].inAlternateTimezone) {
                clone[key] = this[key].inAlternateTimezone(timezone);
            }
        }

        return clone;
    }
}
